import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import type { OnyxDate } from '@onyx/angular';
import {
  FormHelper,
  OnyxDatepickerComponent,
  onyxDateValidator,
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  onyxMaxNumberValidator,
  onyxMinNumberValidator,
  OnyxSectionComponent,
  OnyxTabsComponent,
  OnyxTextFieldComponent,
} from '@onyx/angular';
import { difference } from 'lodash';
import { filter } from 'rxjs';
import { YES_NO_OPTIONS } from '../../../../common/constants/common/yes-no-options';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import type { InterestRateType } from '../../../../common/enums/interest-rate-type';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import type { PreferencesService } from '../../../../common/services/preferences.service';
import { OwnershipType } from '../../common/enums/ownership-type';

export type FleetOwnershipFormGroup = ReturnType<
  typeof FleetOwnershipFormComponent.buildForm
>;

@Component({
  selector: 'app-fleet-ownership-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxTabsComponent,
    OnyxDropdownComponent,
    AsyncPipe,
    OnyxFormGroupComponent,
    NgTemplateOutlet,
    OnyxTextFieldComponent,
    OnyxDatepickerComponent,
  ],
  templateUrl: './fleet-ownership-form.component.html',
  styleUrl: './fleet-ownership-form.component.scss',
})
export class FleetOwnershipFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  public readonly form = input.required<FleetOwnershipFormGroup>();

  protected readonly I18N = 'fleet.fleetForm.ownership';
  protected readonly FOR_SALE_OPTIONS = YES_NO_OPTIONS;

  protected readonly OwnershipType = OwnershipType;

  protected readonly ownershipTypes$ = this.dictionariesService.getDictionary(
    DictionaryCode.OWNERSHIP_TYPE,
  );
  protected readonly currencies$ = this.dictionariesService.getDictionary(
    DictionaryCode.CURRENCY,
  );
  protected readonly interestRateTypes$ =
    this.dictionariesService.getDictionary(DictionaryCode.INTEREST_RATE_TYPE);

  protected type!: Signal<OwnershipType | null>;

  public ngOnInit(): void {
    const controls = this.form().controls;
    const leasingLoanRentalControls = [
      controls.selfDeposit,
      controls.installment,
      controls.firstAndLastInstallmentDate,
    ];
    const leasingLoanControls = [
      controls.buyout,
      controls.margin,
      controls.interestRateType,
    ];

    const typeControls = {
      [OwnershipType.OWN]: [controls.vehicleValue, controls.depreciationPeriod],
      [OwnershipType.LEASING]: [
        controls.leasingProfile,
        ...leasingLoanRentalControls,
        ...leasingLoanControls,
      ],
      [OwnershipType.LOAN]: [
        controls.loanProfile,
        ...leasingLoanRentalControls,
        ...leasingLoanControls,
      ],
      [OwnershipType.RENTAL]: [
        controls.rentalProfile,
        ...leasingLoanRentalControls,
      ],
    };

    const typeControl = controls.type;
    this.type = toSignal(typeControl.valueChanges, {
      initialValue: typeControl.value,
      injector: this.injector,
    });

    effect(
      () => {
        const type = this.type();
        const enabledControls = [
          controls.isForSale,
          typeControl,
          ...(type ? typeControls[type] : []),
        ];
        const disabledControls = difference(
          Object.values(controls),
          enabledControls,
        );

        ValidationHelper.enableControls(enabledControls);
        ValidationHelper.disableControls(disabledControls, { reset: true });
      },
      { injector: this.injector },
    );

    const selfDepositCurrencyControl = this.form().get('selfDeposit.currency')!;
    selfDepositCurrencyControl.valueChanges
      .pipe(
        filter(() => selfDepositCurrencyControl.enabled),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((currency) =>
        this.form().patchValue({
          buyout: { currency },
          installment: { currency },
        }),
      );
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferencesService: PreferencesService,
  ) {
    return fb.group({
      isForSale: fb.control(false, [Validators.required]),
      type: fb.control<OwnershipType | null>(null, [Validators.required]),

      // OwnershipType.OWN
      vehicleValue: FormHelper.buildAmountForm(fb, preferencesService, {
        minValue: 0,
      }),
      depreciationPeriod: fb.control<OnyxDate | null>(null, [
        Validators.required,
        onyxDateValidator,
      ]),

      // OwnershipType.LEASING, OwnershipType.LOAN, OwnershipType.RENTAL
      leasingProfile: fb.control<string | null>(null),
      loanProfile: fb.control<string | null>(null),
      rentalProfile: fb.control<string | null>(null),
      selfDeposit: FormHelper.buildAmountForm(fb, preferencesService, {
        minValue: 0,
      }),
      installment: FormHelper.buildAmountForm(fb, preferencesService, {
        minValue: 0,
      }),
      firstAndLastInstallmentDate: fb.control<OnyxDate | null>(null, [
        Validators.required,
        onyxDateValidator,
      ]),

      // OwnershipType.LEASING, OwnershipType.LOAN
      buyout: FormHelper.buildAmountForm(fb, preferencesService, {
        minValue: 0,
      }),
      margin: fb.control<number | null>(null, [
        Validators.required,
        onyxMinNumberValidator(0, 2),
        onyxMaxNumberValidator(100, 2),
      ]),
      interestRateType: fb.control<InterestRateType | null>(null, [
        Validators.required,
      ]),
    });
  }
}
